import { useEffect, useMemo, useState } from "react";
import { usePermissions } from "../rbac/PermissionsContext";
import { getAuthJson } from "../utils/authStorage";
import { useMyTeamNavVisibility } from "./useMyTeamNavVisibility";

/**
 * RBAC + company-plan visibility for employee left navigation.
 * Rules mirror RouteGate on /employee/* routes and EmployeeSidebar.jsx.
 */
export function useEmployeeLeftNavVisibility() {
  const { hasScreenAccess, hasAnyModuleAccess, isAdmin, loading, snapshot } =
    usePermissions();
  const showMyTeam = useMyTeamNavVisibility();
  const [companyData, setCompanyData] = useState(() => getAuthJson("companyData"));

  useEffect(() => {
    const syncCompany = () => setCompanyData(getAuthJson("companyData"));
    syncCompany();
    const onCompanyUpdate = (e) => {
      if (e?.detail) setCompanyData(e.detail);
      else syncCompany();
    };
    window.addEventListener("companyDataUpdated", onCompanyUpdate);
    return () => window.removeEventListener("companyDataUpdated", onCompanyUpdate);
  }, []);

  return useMemo(() => {
    const rbacReady = Boolean(snapshot) || !loading;

    if (!rbacReady) {
      return {
        ready: false,
        home: false,
        profile: false,
        attendance: false,
        payroll: false,
        reports: false,
        myAssets: false,
        myTeam: false,
        companyData,
      };
    }

    return {
      ready: true,
      home: isAdmin || hasScreenAccess("home", "dashboard", "view"),
      profile: isAdmin || hasAnyModuleAccess("profile"),
      attendance:
        companyData?.module_attendance === 1 &&
        (isAdmin || hasScreenAccess("ess_attendance", "self_service", "view")),
      payroll:
        companyData?.module_payroll === 1 &&
        (isAdmin || hasScreenAccess("ess_payroll", "self_service", "view")),
      reports:
        companyData?.module_reports === 1 &&
        (isAdmin || hasScreenAccess("ess_reports", "reports", "view")),
      myAssets:
        isAdmin || hasScreenAccess("ess_assets", "self_service", "view"),
      myTeam: showMyTeam,
      companyData,
    };
  }, [
    companyData,
    hasAnyModuleAccess,
    hasScreenAccess,
    isAdmin,
    loading,
    showMyTeam,
    snapshot,
  ]);
}
